import { cache } from 'react';
import { getGfeCategoryCached } from '@/lib/gfe/category';
import { normalizeRouteForPageConfig } from '@/logic/page-config/normalize-route-for-page-config';
import type { GfeCategoryResponse } from '@/types/gfe/category';
import { ROUTES } from '@/utils/constants';
import { ensureProductPrefixedUid } from '@/utils/url/product-prefix';
import { removeProductFromUid } from '@/utils/url/url';
import { GFE_CANARY } from '@/logic/experiments/canaries';
import { getCanaryVariant } from '@/logic/experiments/get-canary-variant';

export type CategoryChromeSource = { source: 'gfe'; response: GfeCategoryResponse } | { source: 'aggregation' };

const AGGREGATION_SOURCE: CategoryChromeSource = { source: 'aggregation' };

export const getCategorySlugFromRoute = (currentRoute: string): string | null => {
    const normalized = normalizeRouteForPageConfig(currentRoute);

    if (!normalized.startsWith(ROUTES.CATEGORY) || normalized.startsWith(ROUTES.CATEGORY_HUB)) {
        return null;
    }

    const slug = normalized.slice(ROUTES.CATEGORY.length).split('/')[0];
    return slug ? removeProductFromUid(slug) : null;
};

export const getCategoryChromeSource = cache(async (currentRoute: string): Promise<CategoryChromeSource> => {
    const slug = getCategorySlugFromRoute(currentRoute);
    if (!slug) {
        return AGGREGATION_SOURCE;
    }

    // Rollout gate. Reads the variant the middleware stamped, so it costs a header lookup rather
    // than a request, and cannot disagree with the identical check in `getResolvedCategory`.
    if ((await getCanaryVariant(GFE_CANARY)) !== 'gfe') {
        return AGGREGATION_SOURCE;
    }

    const categoryUid = ensureProductPrefixedUid(slug);

    try {
        const response = await getGfeCategoryCached(categoryUid);
        // Rollout-observability signal: one line per category request (this fn is `cache()`-d, so it
        // fires once and covers every chrome adaptor + the page content that read this decision).
        // Uses `console.warn` deliberately: `next.config.mjs` strips all `console.*` except
        // `error`/`warn` in production builds, so `info`/`log` would never reach Datadog.
        // Filter in Datadog on `(getCategoryChromeSource) source=gfe` vs `source=aggregation`.
        console.warn(`(getCategoryChromeSource) categoryUid=${categoryUid} source=${response ? 'gfe' : 'aggregation'}`);
        return response ? { source: 'gfe', response } : AGGREGATION_SOURCE;
    } catch (error) {
        // Any GFE fault falls open to aggregation, matching the category page's own fallback.
        console.error(`(getCategoryChromeSource) GFE fetch failed for ${categoryUid}, using aggregation:`, error);
        return AGGREGATION_SOURCE;
    }
});
